import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  CheckCircle, ArrowRight, Shield, AlertTriangle, Clock, FileText,
  Star, TrendingDown, Bell, Mail, Gavel
} from 'lucide-react';
import { analytics } from '../../lib/ga4';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IMAGES = {
  impayes: 'https://static.prod-images.emergentagent.com/jobs/e195386f-f80b-4538-af09-25780105987d/images/ec8c975d55c80fda523fad61a6b783f46153095c79127726a0ab22f9992f12cd.png'
};

const LandingImpayes = () => {
  const [searchParams] = useSearchParams();
  
  useEffect(() => {
    const utmSource = searchParams.get('utm_source');
    analytics.trackEvent('landing_view', {
      page: 'impayes',
      utm_source: utmSource
    });

    fetch(`${API_URL}/api/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'landing_view',
        details: { page: 'impayes', referrer: document.referrer }
      })
    }).catch(() => {});
  }, [searchParams]);

  const handleCTAClick = (buttonName) => {
    analytics.trackButtonClick(buttonName, 'landing_impayes');
  };

  return (
    <>
      <Helmet>
        <title>Réduire les Impayés | Relances Automatiques | SmartFacture AI</title>
        <meta name="description" content="Réduisez vos impayés de 60% avec les relances automatiques. Récupérez votre argent sans effort. Essai gratuit 7 jours." />
        <link rel="canonical" href="https://smartfacture-ai.com/landing/impayes" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-white border-b border-slate-100 py-4 px-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900">SmartFacture AI</span>
            </Link>
            <Link 
              to="/register"
              onClick={() => handleCTAClick('header_cta')}
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
            >
              Essai gratuit
            </Link>
          </div>
        </header>

        {/* Hero */}
        <section className="bg-gradient-to-br from-red-600 to-orange-600 text-white py-16 lg:py-24">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 rounded-full text-sm mb-6">
                  <AlertTriangle className="h-4 w-4" />
                  25% des faillites sont dues aux impayés
                </div>
                
                <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                  Marre des impayés ?
                  <span className="text-orange-200"> Automatisez vos relances</span>
                </h1>
                
                <p className="text-xl text-red-100 mb-8">
                  Récupérez votre argent <strong>sans effort</strong>. Nos clients réduisent leurs impayés de <strong>60% en moyenne</strong>.
                </p>

                <div className="space-y-3 mb-8">
                  {[
                    'Relances automatiques J+1, J+7, J+14, J+30',
                    'Emails personnalisés professionnels',
                    'Mise en demeure générée automatiquement',
                    'Tableau de bord des créances en temps réel'
                  ].map((benefit, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-orange-300 flex-shrink-0" />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link 
                    to="/register"
                    onClick={() => handleCTAClick('hero_cta')}
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-red-600 hover:bg-red-50 text-lg font-semibold rounded-xl shadow-xl transition-all"
                  >
                    Récupérer mon argent
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </div>

                <p className="text-sm text-red-200 mt-4">
                  Essai gratuit 7 jours • ROI garanti
                </p>
              </div>

              <div className="hidden lg:block">
                <img 
                  src={IMAGES.impayes}
                  alt="Réduire les impayés avec SmartFacture AI"
                  className="rounded-2xl shadow-2xl"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 px-4 bg-slate-900 text-white">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8 text-center">
              {[
                { value: '56 Mds€', label: 'Impayés annuels en France' },
                { value: '12 jours', label: 'Retard moyen de paiement' },
                { value: '30%', label: 'Factures payées en retard' },
                { value: '-60%', label: "D'impayés avec SmartFacture AI", highlight: true }
              ].map((stat, i) => (
                <div key={i}>
                  <p className={`text-4xl font-bold ${stat.highlight ? 'text-green-400' : ''}`}>{stat.value}</p>
                  <p className="text-slate-400 text-sm mt-2">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">
              Comment ça fonctionne ?
            </h2>

            <div className="grid md:grid-cols-4 gap-6">
              {[
                { 
                  icon: Clock, 
                  step: '1',
                  title: 'J+1 : Rappel amical', 
                  desc: 'Email automatique cordial rappelant l\'échéance passée' 
                },
                { 
                  icon: Bell, 
                  step: '2',
                  title: 'J+7 : Relance ferme', 
                  desc: 'Ton plus direct, rappel des pénalités de retard' 
                },
                { 
                  icon: Mail, 
                  step: '3',
                  title: 'J+14 : Dernière relance', 
                  desc: 'Mise en garde avant action formelle' 
                },
                { 
                  icon: Gavel, 
                  step: '4',
                  title: 'J+30 : Mise en demeure', 
                  desc: 'Courrier juridique généré automatiquement' 
                }
              ].map((item, i) => (
                <div key={i} className="relative">
                  <div className="bg-slate-50 rounded-2xl p-6 h-full">
                    <div className="absolute -top-3 -left-3 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                      {item.step}
                    </div>
                    <item.icon className="h-10 w-10 text-red-500 mb-4" />
                    <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-slate-600 text-sm">{item.desc}</p>
                  </div>
                  {i < 3 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-red-300" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16 px-4 bg-slate-50">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-6">
                  Résultats concrets pour nos clients
                </h2>
                
                <div className="space-y-6">
                  {[
                    { 
                      metric: '-60%', 
                      label: "d'impayés", 
                      desc: 'Réduction moyenne des factures non payées à 60 jours' 
                    },
                    { 
                      metric: '-15 jours', 
                      label: 'délai de paiement', 
                      desc: 'Les clients paient en moyenne 15 jours plus tôt' 
                    },
                    { 
                      metric: '8h/mois', 
                      label: 'économisées', 
                      desc: 'Plus besoin de relancer manuellement vos clients' 
                    }
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm">
                      <div className="text-3xl font-bold text-green-500">{item.metric}</div>
                      <div>
                        <p className="font-semibold text-slate-900">{item.label}</p>
                        <p className="text-slate-600 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h3 className="text-xl font-bold text-slate-900 mb-6">Exemple de relance automatique</h3>
                <div className="bg-slate-50 rounded-xl p-4 font-mono text-sm text-slate-700">
                  <p className="font-bold">Objet : Facture N°2026-042 - Échéance dépassée</p>
                  <br />
                  <p>Bonjour Marie,</p>
                  <br />
                  <p>Sauf erreur de notre part, nous n'avons pas reçu le règlement de la facture N°2026-042 d'un montant de <strong>1 500,00 €</strong> qui était due le 15/03/2026.</p>
                  <br />
                  <p>Merci de procéder au règlement dans les meilleurs délais.</p>
                  <br />
                  <p>Cordialement,<br />Votre nom</p>
                </div>
                <p className="text-sm text-slate-500 mt-4">
                  ✓ Personnalisé avec vos informations
                  ✓ Ton professionnel adapté au délai
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-16 px-4 bg-red-600 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">
              Relances automatiques incluses dès 14€/mois
            </h2>
            <p className="text-xl text-red-100 mb-8">
              Récupérez plus d'argent que le coût de l'abonnement dès le premier mois
            </p>

            <Link 
              to="/register"
              onClick={() => handleCTAClick('pricing_cta')}
              className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-white text-red-600 hover:bg-red-50 text-xl font-semibold rounded-xl shadow-xl transition-all"
            >
              Activer les relances automatiques
              <ArrowRight className="h-6 w-6" />
            </Link>

            <p className="text-red-200 mt-4">Essai gratuit 7 jours • Annulation à tout moment</p>
          </div>
        </section>

        {/* Testimonial */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-green-50 border border-green-200 rounded-2xl p-8 lg:p-12">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <blockquote className="text-xl lg:text-2xl text-slate-700 mb-6">
                "J'avais 15 000€ de créances en retard. En 3 mois avec SmartFacture AI, j'ai récupéré 12 000€ grâce aux relances automatiques. Le logiciel s'est rentabilisé en 1 semaine !"
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-200 rounded-full flex items-center justify-center">
                  <span className="text-green-700 font-bold">JD</span>
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Jean-Pierre D.</p>
                  <p className="text-slate-500">Artisan plombier</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 px-4 bg-slate-900 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Arrêtez de perdre de l'argent
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              Activez les relances automatiques et récupérez vos créances
            </p>
            
            <Link 
              to="/register"
              onClick={() => handleCTAClick('final_cta')}
              className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white text-xl font-semibold rounded-xl shadow-xl transition-all"
            >
              Commencer maintenant
              <ArrowRight className="h-6 w-6" />
            </Link>

            <div className="flex items-center justify-center gap-6 mt-8 text-sm text-slate-400">
              <span className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                ROI garanti
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                Sans engagement
              </span>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-950 text-slate-400 py-8 px-4">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <p>© 2026 SmartFacture AI. Tous droits réservés.</p>
            <div className="flex gap-6 text-sm">
              <Link to="/mentions-legales" className="hover:text-white">Mentions légales</Link>
              <Link to="/cgu" className="hover:text-white">CGU</Link>
              <Link to="/confidentialite" className="hover:text-white">Confidentialité</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingImpayes;
